import {
  Button,
  Icon,
  ModalProps,
  TextBody,
  TextCaption,
  TextTitle,
} from '@pypestream/design-system';
import {
  FC,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';

import { GenericModal } from '../../components';
import { headerColorFor } from '../canvas-view/agent-node-card';
import {
  describeAgentSource,
  type CanvasAgentNode,
} from '../canvas-view/canvas-view-model';
import {
  coreKindFor,
  fitPillCount,
  orderForOverview,
  splitAgents,
  type CoreAgentKind,
} from './design-agents-model';
import knowledgeLogo from './knowledge-agent.svg?url';
import supervisorLogo from './supervisor-agent.svg?url';

import './design-agents.scss';

const LOGOS: Record<CoreAgentKind, string> = {
  supervisor: supervisorLogo,
  knowledge: knowledgeLogo,
};

const PILL_GAP = 10;
/** Until the More button has rendered once, fit against its typical width. */
const DEFAULT_MORE_WIDTH = 116;

/** The agent's canvas color, darkened for white text the way the canvas does. */
const agentStyle = (agent: CanvasAgentNode): CSSProperties =>
  ({
    '--agent': headerColorFor(agent.color) ?? '#62697c',
  }) as CSSProperties;

const AgentLogo: FC<{ kind: CoreAgentKind; size: number }> = ({
  kind,
  size,
}) => (
  <img
    className="c-design-agents__logo"
    src={LOGOS[kind]}
    width={size}
    height={size}
    alt=""
  />
);

const AgentPill: FC<{
  agent: CanvasAgentNode;
  onOpen: (agent: CanvasAgentNode) => void;
}> = ({ agent, onOpen }) => {
  const kind = coreKindFor(agent);
  return (
    <button
      type="button"
      className={`c-design-agents__pill${kind ? ' c-design-agents__pill--core' : ''}`}
      style={agentStyle(agent)}
      title={`Go to ${agent.title} · ${agent.nodeCount} nodes`}
      data-testid="bobcat-solution-overview-agent-card"
      onClick={() => onOpen(agent)}
    >
      {kind && <AgentLogo kind={kind} size={16} />}
      <span className="c-design-agents__pill-name">{agent.title}</span>
      <span className="c-design-agents__pill-count">{agent.nodeCount}</span>
    </button>
  );
};

const AgentCard: FC<{
  agent: CanvasAgentNode;
  onOpen: (agent: CanvasAgentNode) => void;
}> = ({ agent, onOpen }) => {
  const kind = coreKindFor(agent);
  const source = agent.source ? describeAgentSource(agent.source) : null;
  return (
    <button
      type="button"
      className={`c-design-agents__card${kind ? ' c-design-agents__card--core' : ''}`}
      style={agentStyle(agent)}
      aria-label={`Go to ${agent.title}, ${agent.nodeCount} nodes`}
      data-testid="bobcat-all-agents-card"
      onClick={() => onOpen(agent)}
    >
      <span className="c-design-agents__card-head">
        {kind ? (
          <AgentLogo kind={kind} size={20} />
        ) : (
          <Icon name="microagent" size="small" />
        )}
        <span className="c-design-agents__card-name">{agent.title}</span>
        <span className="c-design-agents__card-count">{agent.nodeCount}</span>
        <span className="c-design-agents__card-go">
          Go to agent
          <Icon name="arrow-right" size="xsmall" />
        </span>
      </span>
      {source && agent.source && (
        // Where Create got it (agent templates), and what is left.
        <span
          className={[
            'c-design-agents__card-source',
            agent.source.kind === 'ai' && 'c-design-agents__card-source--ai',
            source.todo && 'c-design-agents__card-source--todo',
          ]
            .filter(Boolean)
            .join(' ')}
          data-testid="bobcat-all-agents-card-source"
        >
          {agent.source.kind === 'ai' && <Icon name="stars" size="xsmall" />}
          {source.todo ? `${source.label} · ${source.todo}` : source.label}
        </span>
      )}
    </button>
  );
};

const AllAgentsModal: FC<{
  open: boolean;
  agents: CanvasAgentNode[];
  onClose: () => void;
  onOpen: (agent: CanvasAgentNode) => void;
}> = ({ open, agents, onClose, onOpen }) => {
  // `open` drives the modal; GenericModal only requires the ref.
  const modalRef = useRef<ModalProps>(null);
  const { core, useCase, totalNodes } = splitAgents(agents);
  return (
    <GenericModal
      modalRef={modalRef}
      open={open}
      size="large"
      onClose={onClose}
      // Choosing an agent unmounts this page; kept out of the portal so the
      // modal goes with it instead of orphaning in <body>.
      usePortal={false}
      test-id="bobcat-all-agents-modal"
    >
      <div className="c-design-agents__modal" slot="header">
        <TextTitle size="small">All agents</TextTitle>
        <TextCaption variant="secondary">
          {agents.length} agents · {totalNodes} nodes. Choose one to open it in
          the table.
        </TextCaption>
      </div>
      <div className="c-design-agents__modal-body">
        {core.length > 0 && (
          <div className="c-design-agents__group">
            <TextCaption variant="tertiary">CORE</TextCaption>
            <div className="c-design-agents__grid">
              {core.map((agent) => (
                <AgentCard key={agent.id} agent={agent} onOpen={onOpen} />
              ))}
            </div>
          </div>
        )}
        {useCase.length > 0 && (
          <div className="c-design-agents__group">
            <TextCaption variant="tertiary">
              USE-CASE AGENTS — {useCase.length}
            </TextCaption>
            <div className="c-design-agents__grid">
              {useCase.map((agent) => (
                <AgentCard key={agent.id} agent={agent} onOpen={onOpen} />
              ))}
            </div>
          </div>
        )}
      </div>
    </GenericModal>
  );
};

/**
 * Every agent in the solution as a pill in its canvas color, two lines at
 * most; the rest sit behind "See N more", which opens them all as cards.
 * Choosing any agent opens its rows in the Build table.
 */
export const DesignAgents: FC<{
  agents: CanvasAgentNode[];
  onOpenAgent: (agent: CanvasAgentNode) => void;
  /** Search text: only agents whose name contains it are listed. */
  filter?: string;
}> = ({ agents, onOpenAgent, filter = '' }) => {
  const needle = filter.trim().toLowerCase();
  const all = orderForOverview(agents);
  const ordered = needle
    ? all.filter((a) => a.title.toLowerCase().includes(needle))
    : all;
  const listRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLElement>(null);
  const widthsRef = useRef<Map<string, number>>(new Map());
  const [shown, setShown] = useState(ordered.length);
  const [modalOpen, setModalOpen] = useState(false);

  const idsKey = ordered
    .map((a) => `${a.id}:${a.title}:${a.nodeCount}`)
    .join('|');

  const fit = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    // Measure every pill's natural width while it's showing; hidden pills
    // keep the width they had.
    list.querySelectorAll<HTMLElement>('[data-agent-id]').forEach((el) => {
      if (!el.hidden && el.offsetWidth > 0) {
        widthsRef.current.set(el.dataset.agentId ?? '', el.offsetWidth);
      }
    });
    const widths = ordered.map((a) => widthsRef.current.get(a.id) ?? 0);
    const moreWidth = moreRef.current?.offsetWidth || DEFAULT_MORE_WIDTH;
    setShown(
      fitPillCount({
        widths,
        containerWidth: list.clientWidth,
        gap: PILL_GAP,
        moreWidth,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  // Show everything once, measure, then trim to two lines. A timeout rather
  // than a frame: frames don't run in a background tab, layout reads do.
  useLayoutEffect(() => {
    setShown(ordered.length);
    const timer = window.setTimeout(fit, 0);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, fit]);

  // Refit when the list's width changes.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;
    let lastWidth = list.clientWidth;
    const onResize = () => {
      if (list.clientWidth === lastWidth) return;
      lastWidth = list.clientWidth;
      fit();
    };
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(onResize);
    observer?.observe(list);
    window.addEventListener('resize', onResize);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [fit]);

  // The More button's label changes its width; fit again once it's real.
  const fittedMoreWidth = useRef(DEFAULT_MORE_WIDTH);
  useLayoutEffect(() => {
    const width = moreRef.current?.offsetWidth ?? 0;
    if (width > 0 && width !== fittedMoreWidth.current) {
      fittedMoreWidth.current = width;
      fit();
    }
  });

  const open = (agent: CanvasAgentNode) => {
    setModalOpen(false);
    onOpenAgent(agent);
  };

  if (needle && ordered.length === 0) {
    return (
      <TextBody size="small" variant="tertiary">
        No agent matches “{filter.trim()}”.
      </TextBody>
    );
  }

  if (ordered.length === 0) {
    return (
      <TextBody size="small" variant="tertiary">
        No agent groups yet — group nodes in Table View or Canvas.
      </TextBody>
    );
  }

  const hiddenCount = ordered.length - shown;

  return (
    <>
      <div className="c-design-agents" ref={listRef}>
        {ordered.map((agent, i) => (
          <span
            key={agent.id}
            className="c-design-agents__slot"
            data-agent-id={agent.id}
            hidden={i >= shown}
          >
            <AgentPill agent={agent} onOpen={open} />
          </span>
        ))}
        {hiddenCount > 0 && (
          <Button
            ref={moreRef as never}
            variant="ghost"
            size="small"
            aria-haspopup="dialog"
            test-id="bobcat-solution-overview-agents-more"
            onClick={() => setModalOpen(true)}
          >
            <Icon slot="prefix" name="expand" size="xsmall" />
            See {hiddenCount} more
          </Button>
        )}
      </div>
      <AllAgentsModal
        open={modalOpen}
        agents={ordered}
        onClose={() => setModalOpen(false)}
        onOpen={open}
      />
    </>
  );
};
